/* eslint import/no-extraneous-dependencies: off */
import { createSlice } from '@reduxjs/toolkit';
import history from '@history';
import _ from 'lodash';
import AccessManagementService from 'app/admin/user/service/AccessManagementService';
import UserLoginService from 'app/admin/user/service/UserLoginService';
import { setInitialSettings, setDefaultSettings } from 'app/store/fuse/settingsSlice';
import { changeLanguage } from 'app/store/i18nSlice';

const safeArr = (v) => (Array.isArray(v) ? v : []);

const normalizeRole = (v) => (v == null ? null : String(v).toLowerCase());

const convertLoggedInClientToAccessConfig = (client) => {
	const clientRoles = safeArr(client?.roles);
	const hasReviewer = clientRoles.some(r => normalizeRole(r) === 'reviewer');

	let serviceList = [
		'Admin',
		'UserProfile',
		'UserAccount',
		'UserRole',
		'Szervezo',
		'Vizsgaztato'
	];

	if (hasReviewer) {
		serviceList = ['Reviewer'];
	}

	return {
		user: {
			id: client?.id ?? null,
			name: client?.name ?? null,
			email: client?.email ?? null,
			profilePicture: client?.profilePicture ?? null,
			currentLocale: client?.currentLocale || 'en',
			whatIfGroupList: [],
			userGroups: safeArr(client?.userGroups),
			roles: clientRoles
		},
		roleList: [{ servicePermissionList: [], contentPermissionList: [], isEnabled: true, key: 'admin', title: 'Admin', isSuperAdmin: true, id: 1 }],
		serviceList,
		serviceLevelMap: serviceList.reduce((acc, s) => ({ ...acc, [s]: 'WriteRead' }), {}),
		settingsMap: null,
		contentTypeLevelMap: {}
	};
};

export const getUserData = () => async (dispatch, getState) => {
	return new Promise((resolve) => {
		AccessManagementService.getLoggedInClient().then(_accessConfig => {
			const client = _accessConfig || {};
			const usersSettings = JSON.parse(localStorage.getItem('userSettings'));
			const accessConfig = convertLoggedInClientToAccessConfig(client);

			const nextRole = accessConfig.serviceList;
			const nextId = accessConfig.user?.id ?? null;

			const prev = getState()?.auth?.user || {};
			const prevId = prev?.data?.id ?? null;
			const prevRole = Array.isArray(prev?.role) ? prev.role.join('|') : '';

			if (prevId === nextId && prevRole === nextRole.join('|')) {
				resolve();
				return;
			}

			dispatch(
				setUserData({
					role: nextRole,
					serviceList: accessConfig.serviceList,
					data: { ...accessConfig.user, settings: usersSettings || {} },
					roleList: accessConfig.roleList,
					isSuperAdmin: accessConfig.isSuperAdmin,
					accessType: accessConfig.accessType,
					settingMap: accessConfig.settingMap,
					serviceLevelMap: accessConfig.serviceLevelMap,
					userGroups: accessConfig.user.userGroups,
					roles: accessConfig.user.roles
				})
			);

			resolve();
		}).catch(() => {
			resolve();
		});
	});
};

export const setUserData = user => async (dispatch) => {
	if (!_.isUndefined(user.data.currentLocale) && user.data.currentLocale !== null) {
		dispatch(changeLanguage(user.data.currentLocale));
	}

	history.location.state = {
		redirectUrl: user.redirectUrl
	};

	dispatch(setDefaultSettings(user.data.settings));
	dispatch(setUser(user));
};

export const logoutUser = () => async (dispatch, getState) => {
	const { user } = getState().auth;

	if (!user.role || user.role.length === 0) {
		return null;
	}

	history.push({ pathname: '#' });
	AccessManagementService.logout();
	dispatch(setInitialSettings());

	return dispatch(userLoggedOut());
};

export const logoutUserSilent = () => async (dispatch) => {
	try {
		await Promise.resolve(AccessManagementService.logout());
	} catch (e) { }

	dispatch(setInitialSettings());

	return dispatch(userLoggedOut());
};

export const updateUserSettings = settings => async (dispatch, getState) => {
	const oldUser = getState().auth.user;
	const user = _.merge({}, oldUser, { data: { settings } });

	dispatch(updateUserData(user));

	return dispatch(setUserData(user));
};

export const updateUserShortcuts = () => async () => {
	// not used
};

export const updateUserData = user => async () => {
	if (!user.role || user.role.length === 0) return;

	localStorage.setItem('userSettings', JSON.stringify(user.data.settings));
};

const initialState = {
	role: [],
	data: {
		name: 'Guest'
	}
};

const userSlice = createSlice({
	name: 'auth/user',
	initialState,
	reducers: {
		setUser: (state, action) => action.payload,
		userLoggedOut: () => initialState
	},
	extraReducers: {}
});

export const { setUser, userLoggedOut } = userSlice.actions;

export default userSlice.reducer;